<template>
  <Modal
    v-model:is-open="overwrite_choices"
    max-width="max-w-[600px] mx-5"
    :use-toast="true"
    :default-success-msg="$t('Choices replaced successfully')">
    <template #form="{ close: closeModal, done: showIsDone }">
      <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        {{ $t("Replace Options?") }}
      </h3>
      <div class="max-w-xl mt-2 text-sm text-gray-500">
        <p>{{ $t("Once you replace options, you can't restore it.") }}</p>
      </div>
      <div class="mt-5 text-center">
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
          @click="confirmReplace(showIsDone)">
          {{ $t("Replace Options") }}
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
          @click="closeModal">
          {{ $t("Cancel") }}
        </button>
      </div>
    </template>
  </Modal>

  <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute left-0 top-0 pointer-events-none" />
  <div class="relative z-10">
    <div class="flex items-center space-x-4 text-2xl font-medium">
      <ClipboardPaste color="black" />
      <div>{{ survey ? $t("Edit") : $t("Create") }} {{ $t("Survey") }}</div>
    </div>
    <div class="text-sm text-common-grey">
      {{ survey ? $t("Edit survey template") : $t("Create a new survey template.") }}
    </div>
  </div>
  <div v-if="step == 1" ref="click_ref" class="w-full mt-6">
    <SendSurveyForm
      v-model:header-color="header_color"
      v-model:header-text-color="header_text_color"
      v-model:page-bg-color="page_bg_color"
      v-model:headline-message="headline_message"
      v-model:headline-subtitle-message="headline_subtitle_message"
      v-model:name="name"
      v-model:use-r-s-email="use_rs_email"
      v-model:from-label="from_label"
      v-model:email-subject="email_subject"
      v-model:selected-records="selected_records"
      v-model:header-message="header_message"
      v-model:success-message="success_message"
      v-model:selected-sender="sender"
      v-model:submission-end-date="submission_end_date"
      v-model:re-submission-end-date="re_submission_end_date"
      v-model:allow-resubmission="allow_resubmission" />
  </div>

  <draggable
    v-if="step == 2"
    v-model="questions"
    filter=".ignore-elements"
    handle=".drag-handle"
    :component-data="{ type: 'transition-group', name: !drag ? 'flip-list' : null }"
    v-bind="dragOptions"
    item-key="id"
    class="w-full mt-10 text-left transition ease-in-out border-light-divider-border dark:border-dark-divider-border duration-side-menu-transition-duration"
    tag="tr"
    @start="drag = true"
    @end="drag = false">
    <template #item="{ element, index }">
      <div class="bg-dark-brand-blue/10 p-2 pb-8 border-2 border-[#2DA7D7] rounded-lg mb-4 border-dashed relative w-full">
        <div v-if="index == questions.length - 1" class="absolute -left-0 -bottom-0.5 w-full bg-[#2DA7D7] h-[2px] rounded-md" />

        <div class="w-full absolute bottom-2 left-[40%] flex justify-center">
          <div class="flex items-center justify-end space-x-2">
            <div>
              {{ element.is_required ? $t("Required") : $t("Not Required") }}
            </div>

            <Switch
              v-model="element.is_required"
              :class="[
                element.is_required ? 'bg-blue' : 'bg-gray-200 dark:bg-dark-screen-background',
                'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
              ]">
              <span
                aria-hidden="true"
                :class="[
                  element.is_required && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-5'
                    : element.is_required
                      ? 'translate-x-5'
                      : 'translate-x-0',
                  'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                ]" />
            </Switch>
          </div>
        </div>

        <div v-if="index == questions.length - 1" class="absolute flex justify-center w-full -bottom-4">
          <button class="btn cancel-btn border-[#2DA7D7]" @click="handleAddNewQuestion()">
            <Plus class="relative w-5 h-5 right-2" /> {{ $t("Add New Question") }}
          </button>
        </div>
        <div class="flex items-center w-full mb-4 space-x-2 text-xs cursor-pointer drag-handle text-common-grey">
          <GripVertical class="w-3 h-3" color="#46545C" />
          <div>{{ $t("Question") }} {{ index + 1 }}</div>
          <div class="!ml-auto" @click="handleDuplicateQuestion(element)">
            <CopyPlus class="w-5 h-5 transition-all ease-in-out hover:scale-125" color="#49606D" />
          </div>
          <div
            v-if="questions.length > 1"
            @click="
              () => {
                questions.splice(index, 1)
                if (!questions.length) {
                  handleAddNewQuestion()
                }
              }
            ">
            <Trash2 class="w-5 h-5 transition-all ease-in-out cursor-pointer hover:text-red-500" />
          </div>
        </div>

        <div class="rounded-md shadow-sm -space-y-px w-[300px] mb-2">
          <SelectInput
            v-model="element.type"
            name="question_type"
            :items="question_types"
            :value="element?.type"
            :allow-null="false"
            value-attr="value"
            label-attr="label"
            :placeholder="$t('Select question type')"
            :label="$t('Select Question Type')"
            input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            @update:currentItem="
              ($event) => {
                handleSelectType($event, element)
              }
            " />
        </div>

        <AutoCompleteInput
          v-if="!element.type?.split('_')?.includes('grid')"
          class="w-[500px]"
          :value="element?.metric?.name"
          :data="getMetrics(element)"
          :placeholder="$t('Start typing to search or create a new question')"
          @changed="
            ($event) => {
              element.metric['name'] = $event
            }
          "
          @chosen="chooseMetric($event, element)"
          @reset="resetMetric(element)" />

        <TextInput
          v-else
          label=""
          :value="element.text"
          :is-box-background="false"
          input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
          name="name"
          type="text"
          :placeholder="$t('Start typing to create a new question')"
          @input="
            ($event) => {
              element.text = $event.target.value
            }
          " />

        <div v-if="element.type?.split('_')?.includes('grid')" class="flex w-full mt-4 space-x-2">
          <div class="w-1/3 mt-10">
            <div v-for="(metric, metric_index) in element.metrics" :key="metric_index" class="relative mb-2 group">
              <div
                v-if="element.metrics.length > 1"
                class="absolute z-10 text-red-500 transition-all duration-300 opacity-0 right-8 top-2 group-hover:opacity-100"
                @click="
                  () => {
                    element.metrics.splice(metric_index, 1)
                    if (metric.id) element.metric_choices = ['']
                  }
                ">
                <Trash2 class="w-5 h-5 transition-all ease-in-out cursor-pointer hover:text-red-500" />
              </div>
              <AutoCompleteInput
                :input-disabled="gridLockedCalc(element) && metric.id"
                :value="metric.name"
                :data="getMetrics(element)"
                :placeholder="$t('Start typing')"
                @changed="
                  ($event) => {
                    metric.name = $event
                  }
                "
                @chosen="chooseMetricInGrid($event, element, metric_index)"
                @reset="resetMetricInGrid(element, metric_index)" />
            </div>
            <div @click="addNewMetricToGrid(element)">
              <Plus
                class="w-8 h-8 cursor-pointer hover:scale-125 transition-all ease-in-out border border-light-brand-blue p-1.5 rounded-full" />
            </div>
          </div>
          <div class="flex w-2/3 space-x-2 overflow-x-scroll">
            <div v-for="(option, option_index) in element.metric_choices" :key="option_index" class="min-w-[140px] pb-6 relative group">
              <div
                v-if="element.metric_choices.length > 1 && !gridLockedCalc(element)"
                class="absolute z-10 text-red-500 transition-all duration-300 opacity-0 top-2 right-2 group-hover:opacity-100"
                @click="element.metric_choices.splice(option_index, 1)">
                <Trash2 class="w-5 h-5 transition-all ease-in-out cursor-pointer hover:text-red-500" />
              </div>
              <TextInput
                :disabled="gridLockedCalc(element)"
                label=""
                :value="option"
                :is-box-background="false"
                input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                type="text"
                class="mb-3"
                :placeholder="$t('Start typing')"
                @input="
                  ($event) => {
                    element.metric_choices[option_index] = $event.target.value
                  }
                " />

              <div v-for="(_, box_index) in element.metrics" :key="box_index">
                <div class="w-5 h-5 mt-2 bg-white rounded-sm ml-14 mb-7" />
              </div>
            </div>
            <div v-if="!gridLockedCalc(element)" @click="addNewMetricOptionToGrid(element)">
              <Plus
                class="w-8 h-8 cursor-pointer hover:scale-125 transition-all ease-in-out border border-light-brand-blue p-1.5 rounded-full" />
            </div>
          </div>
        </div>

        <div v-if="element?.metric?.input_type == 'Select' || element?.metric?.input_type == 'Checklist'" class="mt-4 ml-6">
          <div v-for="(option, o_i) in element.metric.input_options.choices" :key="o_i" class="mb-2">
            <div class="flex items-center space-x-2">
              <TextInput
                :disabled="element?.metric?.id"
                label=""
                :value="option"
                :is-box-background="false"
                input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                type="text"
                :placeholder="$t('Start typing')"
                @input="
                  ($event) => {
                    element.metric.input_options.choices[o_i] = $event.target.value
                  }
                " />
              <div
                v-if="element.metric.input_options?.choices?.length > 1 && !element?.metric?.id"
                @click="element.metric.input_options.choices.splice(o_i, 1)">
                <Trash2 class="w-5 h-5 transition-all ease-in-out cursor-pointer hover:text-red-500" />
              </div>
            </div>
          </div>

          <div v-if="!element?.metric?.id" class="mt-2" @click="addChoice(element)">
            <Plus
              class="w-7 h-7 cursor-pointer hover:scale-125 transition-all ease-in-out border border-light-brand-blue p-1.5 rounded-full" />
          </div>
        </div>
      </div>
    </template>
  </draggable>

  <div class="flex w-full mt-6 space-x-2">
    <button v-if="step == 2" class="btn cancel-btn" @click="step = 1">
      <ArrowRight class="w-5 h-5 mr-2" :class="[$store.state.portfolio.ui_options?.language == 'ar' ? '' : 'rotate-180']" />
      {{ $t("Edit Survey Details") }}
    </button>

    <button :class="{ '!ml-auto': step == 2 }" class="mr-2 text-light-brand-blue" @click="$emit('cancel')">{{ $t("Close") }}</button>

    <button
      v-if="survey && step == 1"
      :class="{ 'opacity-60 pointer-events-none': step_1_incomplete || step_2_incomplete }"
      class="btn primary-btn !ml-auto w-fit"
      @click="submitSurvey">
      {{ $t("Save Survey") }}
    </button>

    <button
      v-if="step == 1"
      :class="[{ 'opacity-60 pointer-events-none': step_1_incomplete }, { '!ml-auto': !survey }]"
      class="btn cancel-btn w-fit"
      @click="step = 2">
      <ArrowRight class="w-5 h-5 mr-2" :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-180' : '']" />
      {{ survey ? $t("Edit") : $t("Add") }} {{ $t("Questions") }}
    </button>

    <button v-else :class="{ 'opacity-60 pointer-events-none': step_2_incomplete }" class="btn primary-btn w-fit" @click="submitSurvey">
      {{ $t("Save Survey") }}
    </button>
  </div>
</template>

<script>
import TextInput from "@/components/shared/TextInput.vue"
import Modal from "@/components/shared/Modal.vue"
import draggable from "vuedraggable"
import AutoCompleteInput from "@/components/shared/AutoCompleteInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import { ClipboardPaste, GripVertical, CopyPlus, Plus, Trash2, ArrowRight } from "lucide-vue-next"
import dataService from "@/services/data.service"
import { v4 as uuidv4 } from "uuid"
import { Switch } from "@headlessui/vue"
import SendSurveyForm from "@/components/forms/SendSurveyForm.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Modal,
    draggable,
    AutoCompleteInput,
    SelectInput,
    TextInput,
    GripVertical,
    CopyPlus,
    Plus,
    Trash2,
    ArrowRight,
    SendSurveyForm,
    Switch,
    ClipboardPaste
  },
  props: {
    // eslint-disable-next-line vue/require-default-prop
    survey: null,
    records: null
  },
  emits: ["done", "cancel"],
  data() {
    return {
      use_rs_email: true,
      drag: false,
      questions: [{ text: "", metric: {}, type: null, is_required: true, id: 1 }],
      name: "",
      sender: null,
      from_label: "",
      email_subject: "",
      selected_records: [],
      header_message: "",
      success_message: "",
      header_color: "#0D8ABA",
      header_text_color: "#ffffff",
      page_bg_color: "transparent",
      headline_message: `${this.$store.state.auth.user.organization.name} ` + this.$t("Sent you a Survey!"),
      headline_subtitle_message:
        this.$t("Please fill out this form. Doing so will help") +
        ` ${this.$store.state.auth.user.organization.name} ` +
        this.$t("become better."),
      submission_end_date: null,
      re_submission_end_date: null,
      allow_resubmission: false,
      step: 1,
      overwrite_choices: false,
      overwrite_data: { item: null, metric: null, index: null }
    }
  },
  computed: {
    step_1_incomplete() {
      if (!this.name) {
        return true
      }
      if (!this.from_label) {
        return true
      }
      if (!this.email_subject) {
        return true
      }
      if (!this.header_message) {
        return true
      }
      if (!this.success_message) {
        return true
      }

      if (!this.headline_message) {
        return true
      }

      if (!this.headline_subtitle_message) {
        return true
      }

      return false
    },

    step_2_incomplete() {
      let incomplete = false
      this.questions.forEach((question) => {
        if (!question.type) {
          incomplete = true
        }
        if (
          question.type == "text_response" ||
          question.type == "text_area" ||
          question.type == "url" ||
          question.type == "csat" ||
          question.type == "nps" ||
          question.type == "number" ||
          question.type == "star_rating" ||
          question.type == "bar_rating" ||
          question.type == "date"
        ) {
          if (!question.metric.name) {
            incomplete = true
          }
        }
        if (question.type == "single_choice") {
          if (!question.metric.name) {
            incomplete = true
          }
          if (!question.metric.input_options?.choices?.length) {
            incomplete = true
          }
          if (question.metric.input_options?.choices?.filter((c) => !c).length) {
            incomplete = true
          }
        }

        if (question.type == "multiple_choice") {
          if (!question.metric.name) {
            incomplete = true
          }
          if (!question.metric.input_options?.choices?.length) {
            incomplete = true
          }
          if (question.metric.input_options?.choices?.filter((c) => !c).length) {
            incomplete = true
          }
        }

        if (question.type?.split("_")?.includes("grid")) {
          if (!question.text) {
            incomplete = true
          }
          if (question.metrics.filter((m) => !m.name).length) {
            incomplete = true
          }
          if (question.metric_choices.filter((c) => !c).length) {
            incomplete = true
          }
        }
      })
      return incomplete
    },

    question_types() {
      return [
        { label: this.$t("Single choice"), value: "single_choice" },
        { label: this.$t("Multiple choice"), value: "multiple_choice" },
        { label: this.$t("Grid - Single choice"), value: "single_choice_grid" },
        { label: this.$t("Grid - Multiple choice"), value: "multiple_choice_grid" },
        { value: "text_response", label: this.$t("Text") },
        { value: "text_area", label: this.$t("Text Area") },
        { value: "url", label: this.$t("URL") },
        { value: "csat", label: this.$t("CSAT") },
        { value: "nps", label: this.$t("NPS") },
        { value: "number", label: this.$t("Number") },
        { value: "bar_rating", label: this.$t("Bar Rating") },
        { value: "star_rating", label: this.$t("Star Rating") },
        { value: "date", label: this.$t("Date") }
      ]
    },
    dragOptions() {
      return {
        animation: 200,
        group: "user header",
        disabled: false,
        ghostClass: "ghost",
        chosenClass: "chosen"
      }
    },
    all_metrics() {
      return this.$store.state.metrics
    }
  },

  created() {
    setTimeout(() => {
      if (this.$refs?.click_ref) {
        this.$refs?.click_ref?.click()
        this.$refs?.click_ref?.focus()
      }
    }, 500)

    if (this.survey) {
      this.populateForm()
    }

    if (this.records.length) {
      this.records.forEach((record_id) => {
        this.$store.dispatch("data/getRecord", record_id).then((res) => {
          this.selected_records.push(res.data)
        })
      })
    }
  },

  methods: {
    confirmReplace(callback) {
      this.overwrite_data.item.metrics[this.overwrite_data.index] = this.overwrite_data.metric
      this.overwrite_data.item.metric_choices = this.overwrite_data.metric.input_options?.choices
      this.overwrite_data = { item: null, metric: null, index: null }
      callback()
    },
    populateForm() {
      this.name = this.survey.name
      this.from_label = this.survey.from_label
      this.use_rs_email = this.survey.use_rs_email
      this.email_subject = this.survey.subject_line
      this.header_message = this.survey.header_message
      this.success_message = this.survey.success_message
      this.header_color = this.survey.survey_page_header_color
      this.header_text_color = this.survey.survey_page_header_text_color
      this.page_bg_color = this.survey.survey_page_background_color
      this.headline_message = this.survey.survey_page_headline_message
      this.headline_subtitle_message = this.survey.survey_page_headline_subtitle_message
      this.allow_resubmission = this.survey.allow_resubmissions
      this.re_submission_end_date = this.survey.resubmissions_end_date
      this.submission_end_date = this.survey.submissions_end_date

      if (this.survey.sender) {
        const found_integration = this.$store.state.integration.integrations
          .filter((i) => (i.name == "outlook" || i.name == "gmail") && i.status == "on")
          .find((int) => int.id == this.survey.sender)
        this.sender = {
          name: found_integration.user_name,
          id: found_integration.id,
          email: found_integration.user_email,
          value: found_integration.id
        }
      }
      const questions_calc = []
      this.survey.survey_layout.forEach((layout_item) => {
        const question_obj = {}

        if (layout_item.type == "single_choice") {
          question_obj["fe_uuid"] = layout_item.question

          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          question_obj["metric"] = this.$utils.getMetricById(question_found?.metric)
          question_obj["id"] = question_found?.id

          if (!question_obj.metric?.input_options?.choices) {
            question_obj.metric.input_options = { choices: question_obj?.metric?.input_options }
          }
          question_obj["type"] = "single_choice"
          question_obj["is_required"] = question_found.is_required
          questions_calc.push(question_obj)
        }

        if (layout_item.type == "multiple_choice") {
          question_obj["fe_uuid"] = layout_item.question
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          question_obj["metric"] = this.$utils.getMetricById(question_found?.metric)
          question_obj["id"] = question_found?.id
          if (!question_obj.metric?.input_options?.choices) {
            question_obj.metric.input_options = { choices: question_obj?.metric?.input_options }
          }
          question_obj["type"] = "multiple_choice"
          question_obj["is_required"] = question_found.is_required
          questions_calc.push(question_obj)
        }

        if (
          layout_item.type == "text_response" ||
          layout_item.type == "text_area" ||
          layout_item.type == "url" ||
          layout_item.type == "nps" ||
          layout_item.type == "csat"
        ) {
          question_obj["fe_uuid"] = layout_item.question
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)

          question_obj["id"] = question_found.id
          question_obj["metric"] = this.$utils.getMetricById(question_found?.metric)
          question_obj["type"] = layout_item.type
          question_obj["is_required"] = question_found.is_required
          questions_calc.push(question_obj)
        }

        if (layout_item.type == "date") {
          question_obj["fe_uuid"] = layout_item.question
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)

          question_obj["id"] = question_found.id
          question_obj["metric"] = this.$utils.getMetricById(question_found?.metric)
          question_obj["type"] = "date"
          question_obj["is_required"] = question_found.is_required
          questions_calc.push(question_obj)
        }

        if (layout_item.type == "number") {
          question_obj["fe_uuid"] = layout_item.question
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          question_obj["id"] = question_found.id
          question_obj["metric"] = this.$utils.getMetricById(question_found?.metric)
          question_obj["type"] = "number"
          question_obj["is_required"] = question_found.is_required
          questions_calc.push(question_obj)
        }

        if (layout_item.type == "star_rating") {
          question_obj["fe_uuid"] = layout_item.question
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          question_obj["id"] = question_found.id
          question_obj["metric"] = this.$utils.getMetricById(question_found?.metric)
          question_obj["type"] = "star_rating"
          question_obj["is_required"] = question_found.is_required
          questions_calc.push(question_obj)
        }

        if (layout_item.type == "bar_rating") {
          question_obj["fe_uuid"] = layout_item.question
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          question_obj["id"] = question_found.id
          question_obj["metric"] = this.$utils.getMetricById(question_found?.metric)
          question_obj["type"] = "bar_rating"
          question_obj["is_required"] = question_found.is_required
          questions_calc.push(question_obj)
        }

        if (layout_item.type?.split("_")?.includes("grid")) {
          question_obj["fe_uuids"] = layout_item.questions
          const metrics_list_calc = []
          let metric_choices_list_calc = null

          layout_item.questions.forEach((question, i) => {
            const question_found = this.survey.questions.find((q) => q.fe_uuid == question)
            const metric_found = this.$utils.getMetricById(question_found.metric)
            metric_found["question_id"] = question_found.id
            metric_found["fe_uuid"] = question_found.fe_uuid
            if (!i) {
              question_obj.text = question_found.question
              question_obj.is_required = question_found.is_required

              if (metric_found.input_options?.choices) {
                metric_choices_list_calc = metric_found.input_options?.choices
              } else {
                metric_choices_list_calc = metric_found.input_options
              }
            }
            metrics_list_calc.push(metric_found)
          })
          question_obj["metrics"] = metrics_list_calc
          question_obj["metric_choices"] = metric_choices_list_calc
          question_obj["type"] = layout_item.type
          questions_calc.push(question_obj)
        }
      })

      this.questions = questions_calc
    },
    gridLockedCalc(item) {
      return item.metrics.filter((m) => m.id).length
    },
    addNewMetricToGrid(item) {
      item.metrics.push({})
    },
    addNewMetricOptionToGrid(item) {
      item.metric_choices.push("")
    },
    getMetrics(element) {
      if (element.type?.split("_").includes("grid")) {
        if (this.gridLockedCalc(element)) {
          return []
        }
        return this.all_metrics.filter((m) => m.input_type == "Select")
      }

      if (element.type == "multiple_choice") {
        return this.all_metrics.filter((m) => m.input_type == "Checklist")
      }

      if (element.type == "single_choice") {
        return this.all_metrics.filter((m) => m.input_type == "Select")
      }

      if (element.type == "text_response") {
        return this.all_metrics.filter((m) => m.input_type == "Text")
      }

      if (element.type == "text_area") {
        return this.all_metrics.filter((m) => m.input_type == "Textarea")
      }

      if (element.type == "url") {
        return this.all_metrics.filter((m) => m.input_type == "URL")
      }

      if (element.type == "number" || element.type == "csat" || element.type == "nps") {
        return this.all_metrics.filter((m) => m.input_type == "Number")
      }

      if (element.type == "star_rating") {
        return this.all_metrics.filter((m) => m.input_type == "StarRating")
      }

      if (element.type == "bar_rating") {
        return this.all_metrics.filter((m) => m.input_type == "BarRating")
      }
    },
    chooseMetricInGrid(metric, item, index) {
      if (metric.input_options?.choices) {
        if (item.metric_choices.filter((c) => c).length) {
          this.overwrite_choices = true
          this.overwrite_data = { metric, item, index }
        } else {
          item.metrics[index] = metric
          item.metric_choices = metric.input_options?.choices
        }
      }
    },
    chooseMetric(metric, item) {
      item.metric = metric
    },
    resetMetricInGrid(item, index) {
      item.metrics[index] = {}
      if (!item.metrics.filter((m) => m.id).length) {
        item.metric_choices = [""]
      }
      // this.handleSelectType(item.type, item)
    },
    resetMetric(item) {
      item.metric = {}
      this.handleSelectType(item.type, item)
    },
    addChoice(element) {
      element.metric.input_options.choices.push("")
    },

    handleSelectType(type, item) {
      if (type == "single_choice") {
        item.metrics = []
        item["metric_choices"] = []
        item.metric["input_type"] = "Select"
        item.metric["input_options"] = { choices: [] }
      } else if (type == "multiple_choice") {
        item.metrics = []
        item["metric_choices"] = []
        item.metric["input_type"] = "Checklist"
        item.metric["input_options"] = { choices: [] }
      } else if (type == "single_choice_grid") {
        item.metric = {}
        item.metrics = [{ input_type: "Select", name: "" }]
        item["metric_choices"] = [""]
      } else if (type == "multiple_choice_grid") {
        item.metric = {}
        item.metrics = [{ input_type: "Checklist", name: "" }]
        item["metric_choices"] = [""]
      } else if (type == "text_response") {
        item.metric = {}
        item.metric["input_type"] = "Text"
        item.metrics = []
        item["metric_choices"] = []
      } else if (type == "text_area") {
        item.metric = {}
        item.metric["input_type"] = "Textarea"
        item.metrics = []
        item["metric_choices"] = []
      } else if (type == "url") {
        item.metric = {}
        item.metric["input_type"] = "URL"
        item.metrics = []
        item["metric_choices"] = []
      } else if (type == "number" || type == "csat" || type == "nps") {
        item.metric = {}
        item.metric["input_type"] = "Number"
        item.metrics = []
        item["metric_choices"] = []
      } else if (type == "star_rating") {
        item.metric = {}
        item.metric["input_type"] = "StarRating"
        item.metrics = []
        item["metric_choices"] = []
      } else if (type == "bar_rating") {
        item.metric = {}
        item.metric["input_type"] = "BarRating"
        item.metrics = []
        item["metric_choices"] = []
      } else if (type == "date") {
        item.metric = {}
        item.metric["input_type"] = "Date"
        item.metrics = []
        item["metric_choices"] = []
      }
    },

    handleAddNewQuestion() {
      this.questions.push({ text: "", metric: {}, type: null, is_required: true, id: this.questions.length + 1 })
    },
    handleDuplicateQuestion(item) {
      this.questions.push(this.$utils.clone({ ...item, id: this.questions.length + 1 }))
    },
    submitSurvey() {
      const questions_list_calc = []

      const survey_layout_calc = []
      this.questions.forEach((question) => {
        if (
          question.type == "single_choice" ||
          question.type == "text_response" ||
          question.type == "text_area" ||
          question.type == "url" ||
          question.type == "nps" ||
          question.type == "csat" ||
          question.type == "date" ||
          question.type == "number" ||
          question.type == "star_rating" ||
          question.type == "bar_rating"
        ) {
          const question_obj = {}
          const uuid_calc = question["fe_uuid"] ? question["fe_uuid"] : uuidv4()
          question_obj["fe_uuid"] = uuid_calc

          if (this.$utils.isUUID(question["id"])) {
            question_obj["id"] = question["id"]
          }
          if (question.metric.id) {
            question_obj["metric"] = question.metric.id
            question_obj["metric_name"] = question.metric.name
            question_obj["question"] = question.metric.name
            if (question.type == "single_choice" || question.type == "star_rating" || question.type == "bar_rating") {
              question_obj["metric_input_options"] = {
                choices: question.metric?.input_options?.choices ? question.metric?.input_options?.choices : []
              }
            }
            question_obj["is_required"] = question.is_required
          } else {
            question_obj["is_required"] = question.is_required
            question_obj["metric_name"] = question.metric.name
            question_obj["question"] = question.metric.name
            question_obj["metric_input_type"] =
              question.type == "single_choice"
                ? "Select"
                : question.type == "date"
                  ? "Date"
                  : question.type == "number"
                    ? "Number"
                    : question.type == "star_rating"
                      ? "StarRating"
                      : question.type == "bar_rating"
                        ? "BarRating"
                        : "Text"
            if (question.type == "single_choice" || question.type == "star_rating" || question.type == "bar_rating") {
              question_obj["metric_input_options"] = {
                choices: question.metric?.input_options?.choices ? question.metric?.input_options?.choices : []
              }
            }
          }
          questions_list_calc.push(question_obj)
          survey_layout_calc.push({ type: question.type, question: uuid_calc })
        }

        if (question.type == "multiple_choice") {
          const question_obj = {}
          const uuid_calc = uuidv4()

          question_obj["fe_uuid"] = question["fe_uuid"] ? question["fe_uuid"] : uuid_calc
          if (this.$utils.isUUID(question["id"])) {
            question_obj["id"] = question["id"]
          }
          if (question.metric.id) {
            question_obj["metric"] = question.metric.id
            question_obj["metric_name"] = question.metric.name
            question_obj["question"] = question.metric.name
            question_obj["metric_input_options"] = { choices: question.metric.input_options.choices }
            question_obj["is_required"] = question.is_required
          } else {
            question_obj["is_required"] = question.is_required
            question_obj["metric_name"] = question.metric.name
            question_obj["question"] = question.metric.name
            question_obj["metric_input_options"] = {
              choices: question.metric?.input_options?.choices ? question.metric?.input_options?.choices : []
            }
            question_obj["metric_input_type"] = "Checklist"
          }
          questions_list_calc.push(question_obj)
          survey_layout_calc.push({ type: question.type, question: question_obj["fe_uuid"] })
        }

        if (question.type?.split("_")?.includes("grid")) {
          const uuids = []

          question.metrics.forEach((question_metric) => {
            const question_obj = {}
            const uuid_calc = question_metric["fe_uuid"] ? question_metric["fe_uuid"] : uuidv4()

            if (question_metric["question_id"]) {
              question_obj["id"] = question_metric["question_id"]
            }
            uuids.push(uuid_calc)
            question_obj["fe_uuid"] = uuid_calc

            if (question_metric.id) {
              question_obj["metric"] = question_metric.id
              question_obj["metric_name"] = question_metric.name
              question_obj["metric_input_options"] = { choices: question.metric_choices }
              question_obj["is_required"] = question.is_required
              question_obj["question"] = question.text
            } else {
              question_obj["metric_name"] = question_metric.name
              question_obj["is_required"] = question.is_required
              question_obj["question"] = question.text
              question_obj["metric_input_type"] = question.type == "single_choice_grid" ? "Select" : "Checklist"
              question_obj["metric_input_options"] = { choices: question.metric_choices }
            }
            questions_list_calc.push(question_obj)
          })

          survey_layout_calc.push({ type: question.type, questions: uuids })
        }
      })

      const data = {}
      data["records"] = this.selected_records.map((r) => r.id)
      data["name"] = this.name
      data["from_label"] = this.from_label
      data["header_message"] = this.header_message
      data["subject_line"] = this.email_subject
      data["success_message"] = this.success_message
      data["questions"] = questions_list_calc
      data["survey_layout"] = survey_layout_calc
      data["survey_page_header_color"] = this.header_color
      data["survey_page_header_text_color"] = this.header_text_color
      data["survey_page_background_color"] = this.page_bg_color
      data["use_rs_email"] = this.use_rs_email
      data["survey_page_headline_message"] = this.headline_message
      data["survey_page_headline_subtitle_message"] = this.headline_subtitle_message
      data["submissions_end_date"] = this.submission_end_date
      data["allow_resubmissions"] = this.allow_resubmission
      if (this.allow_resubmission) {
        data["resubmissions_end_date"] = this.re_submission_end_date
      } else {
        data["resubmissions_end_date"] = null
      }

      if (this.sender) {
        data["sender"] = this.sender.id
      }

      if (this.survey) {
        dataService.editSurvey(this.survey.id, data).then(() => {
          this.$emitter.emit(ALL_EMITTERS.SURVEY_CREATED_INSIDE_SURVEY_SELECTION_FORM)
          this.$emitter.emit(ALL_EMITTERS.SURVEY_CREATED_INSIDE_SURVEYS_LIST)
          setTimeout(() => {
            this.$store.dispatch("data/getMetrics")
          }, 1000)
          this.$emit("done")
        })
      } else {
        dataService.createSurvey(data).then(() => {
          this.$emitter.emit(ALL_EMITTERS.SURVEY_CREATED_INSIDE_SURVEY_SELECTION_FORM)
          this.$emitter.emit(ALL_EMITTERS.SURVEY_CREATED_INSIDE_SURVEYS_LIST)
          setTimeout(() => {
            this.$store.dispatch("data/getMetrics")
          }, 1000)
          this.$emit("done")
        })
      }
    }
  }
}
</script>
